/**
 * advisor-admin.js — the plan advisor's company rules page (admin/)
 * (spec docs/superpowers/specs/2026-10-12-plan-advisor-design.md, "Admin").
 *
 * Edits the company rule set, the minimum fit and the fit weights, shows the version history, and
 * previews which rules apply in a chosen state and how a given plan resolves. The server is the
 * only validator and the only judge of who is an admin (PUT /agents/advisor-rules); this page just
 * edits a local copy and saves it with the version it was opened at, so two admins can never
 * overwrite each other silently.
 */

import { escapeHtml } from './format.js';
import { authHeaders } from './agent-identity.js';
import { getStoredIdToken } from '../auth-config.js';
import { TIER_LABELS, DEFAULT_WEIGHTS, DEFAULT_FIT_FLOOR, readRuleSet, resolvePlanRule } from './advisor-rules.js';

const API_BASE = '';
const RULES_URL = `${API_BASE}/agents/advisor-rules`;
// The live site's WAF refuses request bodies over 8 KB, and the server refuses the same size.
export const MAX_BODY_BYTES = 8 * 1024;

// Mirrors advisor-rules-model.js PRODUCT_TYPES (plan-classify.js getProductType values).
export const PRODUCT_TYPE_LABELS = {
  aca: 'ACA Marketplace',
  'major-medical': 'Major medical',
  stm: 'Short-term medical',
  gap: 'Gap',
  'limited-medical': 'Limited medical',
  'hospital-indemnity': 'Hospital indemnity',
  dental: 'Dental',
  vision: 'Vision',
  accident: 'Accident',
  'critical-illness': 'Critical illness',
  'specified-disease': 'Specified disease',
  'term-life': 'Term life',
  wellness: 'Wellness',
  additional: 'Additional',
};
export const WEIGHT_LABELS = {
  price: 'Price', coverage: 'Coverage', suitability: 'Suitability', doctorsDrugs: 'Doctors and drugs', wants: 'Wants',
};
// The carrier names plans carry after carrierDisplayLabel (plan-classify.js), offered as suggestions.
export const KNOWN_CARRIERS = [
  'Allstate Health', 'Ambetter', 'AmeriHealth', 'Cigna', 'Florida Blue', 'Florida Blue HMO', 'Iron Health',
  'Flex Benefits', 'LifeX', 'Manhattan Life', 'Molina', 'Oscar', 'Pan-American Life', 'Pivot Health', 'UHOne',
];
export const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS',
  'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC',
  'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

// ---------- pure helpers (tested) ----------

export function describeTarget(target) {
  const t = target || {};
  if (t.planId) return 'Plan ' + t.planId;
  const parts = [];
  if (t.carrier) parts.push(t.carrier);
  if (t.productType) parts.push(PRODUCT_TYPE_LABELS[t.productType] || t.productType);
  return parts.length ? parts.join(' · ') : 'Nothing';
}

export function describeAction(rule) {
  if (!rule) return 'Standard (no rule)';
  if (rule.action === 'hide') return 'Hide';
  return 'Tier ' + rule.tier + ': ' + (TIER_LABELS[rule.tier] || '');
}

function newRuleId(existing) {
  const taken = new Set((existing || []).map((r) => r.id));
  let id;
  do { id = 'r' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6); } while (taken.has(id));
  return id;
}

function parseStates(text) {
  return [...new Set(String(text || '').toUpperCase().split(/[^A-Z]+/).filter(Boolean))];
}

/**
 * Build a rule from the add-rule form's values. Returns { rule } or { error } with a message the
 * page shows next to the form. The server re-validates everything on save.
 */
export function ruleFromForm(values, existingRules) {
  const v = values || {};
  const planId = String(v.planId || '').trim();
  const carrier = String(v.carrier || '').trim();
  const productType = String(v.productType || '').trim();
  const target = {};
  if (planId) {
    if (carrier || productType) return { error: 'A plan rule names only the plan id. Clear the carrier and product type.' };
    target.planId = planId;
  } else {
    if (carrier) target.carrier = carrier;
    if (productType) {
      if (!PRODUCT_TYPE_LABELS[productType]) return { error: 'Pick a product type from the list.' };
      target.productType = productType;
    }
  }
  if (!target.planId && !target.carrier && !target.productType) return { error: 'Name a plan id, a carrier or a product type.' };
  const states = parseStates(v.states);
  const bad = states.filter((s) => !US_STATES.includes(s));
  if (bad.length) return { error: 'Unknown state: ' + bad.join(', ') };
  const action = v.action === 'hide' ? 'hide' : 'tier';
  const rule = { id: newRuleId(existingRules), level: 'company', target, action, note: String(v.note || '').trim().slice(0, 200) };
  if (action === 'tier') {
    const tier = Number(v.tier);
    if (![1, 2, 3].includes(tier)) return { error: 'Pick a tier.' };
    rule.tier = tier;
  }
  if (states.length) rule.states = states;
  return { rule };
}

/** The rules that can apply to a quote in this state, most specific first (as precedence reads). */
export function rulesForState(ruleSet, state) {
  const rules = readRuleSet(ruleSet).rules;
  const weight = (r) => (r.target.planId ? 8 : r.target.carrier && r.target.productType ? 6 : r.target.carrier ? 4 : 2) +
    (r.states && r.states.length ? 1 : 0);
  return rules
    .filter((r) => !(r.states && r.states.length) || (state && r.states.includes(state)))
    .sort((a, b) => weight(b) - weight(a));
}

/** How one plan resolves in a state: the same resolver the results page runs. */
export function previewPlan(ruleSet, plan, state) {
  const p = plan || {};
  return resolvePlanRule(
    { planId: String(p.planId || '').trim(), carrier: String(p.carrier || '').trim(), productType: p.productType || null },
    readRuleSet(ruleSet),
    state || null,
  );
}

/** The PUT body, and whether it fits under the size limit. */
export function savePayload(expectedVersion, draft) {
  const body = JSON.stringify({
    expectedVersion,
    ruleSet: { rules: draft.rules, fitFloor: draft.fitFloor, weights: draft.weights },
  });
  const bytes = new TextEncoder().encode(body).length;
  return { body, bytes, fits: bytes <= MAX_BODY_BYTES };
}

// ---------- network ----------

/** GET the rules with history. Resolves to { ruleSet, isAdmin, history } or { status } on failure. */
export async function fetchAdminData(fetchImpl = fetch) {
  try {
    const res = await fetchImpl(RULES_URL + '?history=1', { headers: authHeaders() });
    if (!res.ok) return { status: res.status };
    const body = await res.json();
    return body && body.data ? body.data : { status: 500 };
  } catch (e) {
    return { status: 0 };
  }
}

/**
 * PUT the rule set. Resolves to { ok: true, ruleSet } or { ok: false, status, message, errors,
 * currentVersion }.
 */
export async function putRuleSet(expectedVersion, draft, fetchImpl = fetch) {
  const { body, fits } = savePayload(expectedVersion, draft);
  if (!fits) return { ok: false, status: 413, message: 'The rule set is over the 8 KB limit. Remove some rules or shorten notes.' };
  try {
    const res = await fetchImpl(RULES_URL, { method: 'PUT', headers: authHeaders(), body });
    let json = null;
    try { json = await res.json(); } catch (e) { json = null; }
    if (res.ok) return { ok: true, ruleSet: json && json.data && json.data.ruleSet };
    const details = json && json.details;
    return {
      ok: false,
      status: res.status,
      message: (json && json.error) || 'Save failed',
      errors: Array.isArray(details) ? details : [],
      currentVersion: details && !Array.isArray(details) ? details.currentVersion : undefined,
    };
  } catch (e) {
    return { ok: false, status: 0, message: 'Could not reach the server. Your changes are still on this page.' };
  }
}

// ---------- page ----------

function options(list, selected, blankLabel) {
  return (blankLabel != null ? '<option value="">' + escapeHtml(blankLabel) + '</option>' : '') +
    list.map(([v, l]) => '<option value="' + escapeHtml(v) + '"' + (String(selected) === String(v) ? ' selected' : '') + '>' +
      escapeHtml(l) + '</option>').join('');
}

const PRODUCT_OPTIONS = Object.keys(PRODUCT_TYPE_LABELS).map((k) => [k, PRODUCT_TYPE_LABELS[k]]);
const STATE_OPTIONS = US_STATES.map((s) => [s, s]);

function rulesTableHtml(rules) {
  if (!rules.length) return '<p class="advisor-admin__empty">No company rules yet. Every plan shows at the standard tier.</p>';
  return '<table class="advisor-admin__table advisor-admin__rules"><thead><tr><th>Applies to</th><th>States</th><th>Action</th><th>Note</th><th></th></tr></thead><tbody>' +
    rules.map((r, i) => '<tr data-rule="' + escapeHtml(r.id) + '">' +
      '<td>' + escapeHtml(describeTarget(r.target)) + '</td>' +
      '<td>' + escapeHtml(r.states && r.states.length ? r.states.join(', ') : 'All') + '</td>' +
      '<td><span class="advisor-admin__action advisor-admin__action--' + escapeHtml(r.action === 'hide' ? 'hide' : 'tier' + r.tier) + '">' +
      escapeHtml(describeAction(r)) + '</span></td>' +
      '<td>' + escapeHtml(r.note || '') + '</td>' +
      '<td><button type="button" class="btn-text-link" data-remove-rule="' + i + '">Remove</button></td></tr>').join('') +
    '</tbody></table>';
}

function addRuleFormHtml() {
  return '<form class="advisor-admin__add" data-add-rule novalidate>' +
    '<label>Carrier <input name="carrier" list="advisorCarriers" autocomplete="off"></label>' +
    '<datalist id="advisorCarriers">' + KNOWN_CARRIERS.map((c) => '<option value="' + escapeHtml(c) + '">').join('') + '</datalist>' +
    '<label>Product type <select name="productType">' + options(PRODUCT_OPTIONS, '', 'Any') + '</select></label>' +
    '<label>or Plan id <input name="planId" autocomplete="off"></label>' +
    '<label>States <input name="states" placeholder="All, or e.g. FL, TX" autocomplete="off"></label>' +
    '<label>Action <select name="action">' + options([['tier', 'Tier'], ['hide', 'Hide']], 'tier') + '</select></label>' +
    '<label>Tier <select name="tier">' + options([[1, '1: Preferred'], [2, '2: Standard'], [3, '3: Last resort']], 1) + '</select></label>' +
    '<label class="advisor-admin__note">Note shown to agents <input name="note" maxlength="200" autocomplete="off"></label>' +
    '<button type="submit" class="btn-primary">Add rule</button>' +
    '<p class="advisor-admin__error" data-add-error role="alert"></p>' +
    '</form>';
}

function settingsHtml(draft) {
  return '<div class="advisor-admin__settings">' +
    '<label>Minimum fit for a suggestion (0 to 100) <input type="number" name="fitFloor" min="0" max="100" step="1" value="' +
    escapeHtml(draft.fitFloor) + '"></label>' +
    '<fieldset><legend>Fit weights</legend>' +
    Object.keys(DEFAULT_WEIGHTS).map((k) => '<label>' + escapeHtml(WEIGHT_LABELS[k]) +
      ' <input type="number" name="weight_' + k + '" min="0" max="100" step="1" value="' + escapeHtml(draft.weights[k]) + '"></label>').join('') +
    '</fieldset></div>';
}

function previewHtml(draft, preview) {
  const state = preview.state || '';
  const applying = rulesForState(draft, state || null);
  const result = previewPlan(draft, preview, state || null);
  const hasPlan = preview.planId || preview.carrier || preview.productType;
  return '<form class="advisor-admin__preview-form" data-preview novalidate>' +
    '<label>State <select name="state">' + options(STATE_OPTIONS, state, 'Choose') + '</select></label>' +
    '<label>Carrier <input name="carrier" list="advisorCarriers" value="' + escapeHtml(preview.carrier || '') + '" autocomplete="off"></label>' +
    '<label>Product type <select name="productType">' + options(PRODUCT_OPTIONS, preview.productType || '', 'Any') + '</select></label>' +
    '<label>Plan id <input name="planId" value="' + escapeHtml(preview.planId || '') + '" autocomplete="off"></label>' +
    '</form>' +
    (hasPlan
      ? '<p class="advisor-admin__verdict" data-verdict>' + escapeHtml(describeTarget(preview.planId ? { planId: preview.planId } : preview)) +
        (state ? ' in ' + escapeHtml(state) : '') + ': <strong>' +
        escapeHtml(result.hidden ? 'Hidden' : 'Tier ' + result.tier + ': ' + TIER_LABELS[result.tier]) + '</strong>' +
        (result.rule ? ' (rule: ' + escapeHtml(describeTarget(result.rule.target)) + (result.rule.note ? ', "' + escapeHtml(result.rule.note) + '"' : '') + ')' : ' (no rule applies)') +
        '</p>'
      : '') +
    '<h3>Rules that apply' + (state ? ' in ' + escapeHtml(state) : ' in every state') + ', most specific first</h3>' +
    (applying.length
      ? '<ol class="advisor-admin__applying">' + applying.map((r) => '<li>' + escapeHtml(describeTarget(r.target)) + ': ' +
        escapeHtml(describeAction(r)) + (r.states && r.states.length ? ' (' + escapeHtml(r.states.join(', ')) + ')' : '') + '</li>').join('') + '</ol>'
      : '<p class="advisor-admin__empty">None.</p>');
}

function historyHtml(history) {
  if (!history || !history.length) return '<p class="advisor-admin__empty">No saved versions yet.</p>';
  return '<table class="advisor-admin__table"><thead><tr><th>Version</th><th>Saved</th><th>By</th><th>Rules</th></tr></thead><tbody>' +
    history.map((h) => '<tr><td>' + escapeHtml(h.version) + '</td><td>' + escapeHtml(h.updatedAt ? new Date(h.updatedAt).toLocaleString() : '') +
      '</td><td>' + escapeHtml(h.updatedBy || '') + '</td><td>' + escapeHtml(h.ruleCount) + '</td></tr>').join('') +
    '</tbody></table>';
}

function messageHtml(title, text) {
  return '<div class="advisor-admin__message"><h2>' + escapeHtml(title) + '</h2><p>' + escapeHtml(text) + '</p>' +
    '<a class="btn-primary" href="../index.html">Go to the quoting tool</a></div>';
}

/**
 * Mount the page into root. Exported for the e2e test; admin/admin.js calls it on load.
 * @param {HTMLElement} root
 * @param {{ fetchImpl?: Function }} [opts]
 */
export async function mountAdmin(root, opts = {}) {
  if (!root) return;
  const fetchImpl = opts.fetchImpl || fetch;
  if (!getStoredIdToken()) {
    root.innerHTML = messageHtml('Sign in first', 'Company rules can only be edited by an advisor admin signed in to the quoting tool. Sign in there, then come back to this page.');
    return;
  }
  root.innerHTML = '<p class="advisor-admin__empty">Loading company rules…</p>';
  const data = await fetchAdminData(fetchImpl);
  if (data.status) {
    root.innerHTML = messageHtml('Could not load the rules', data.status === 401
      ? 'Your sign-in has expired. Sign in to the quoting tool again.'
      : 'The server did not answer. Try again in a moment.');
    return;
  }
  if (!data.isAdmin) {
    root.innerHTML = messageHtml('Admins only', 'Your account is not an advisor admin. Ask an existing admin to add you.');
    return;
  }

  const stored = readRuleSet(data.ruleSet);
  const state = {
    version: stored.version,
    draft: { rules: stored.rules.map((r) => ({ ...r })), fitFloor: stored.fitFloor, weights: { ...stored.weights } },
    history: data.history || [],
    dirty: false,
    status: '',
    statusKind: '',
    preview: { state: '', carrier: '', productType: '', planId: '' },
  };

  function render() {
    const size = savePayload(state.version, state.draft);
    root.innerHTML =
      '<section class="advisor-admin__section"><div class="advisor-admin__bar"><h2>Company rules</h2>' +
      '<span class="advisor-admin__version">Version ' + escapeHtml(state.version) + (state.dirty ? ' · unsaved changes' : '') + '</span></div>' +
      '<p class="advisor-admin__hint">Hide takes a plan off the results page (agents can still reveal it). Tiers rank plans that clear the minimum fit: Preferred first, Last resort last. The most specific rule wins; a state-limited rule beats the same rule everywhere.</p>' +
      rulesTableHtml(state.draft.rules) + addRuleFormHtml() + '</section>' +
      '<section class="advisor-admin__section"><h2>Fit settings</h2>' + settingsHtml(state.draft) + '</section>' +
      '<section class="advisor-admin__section advisor-admin__savebar">' +
      '<button type="button" class="btn-primary" data-save' + (state.dirty && size.fits ? '' : ' disabled') + '>Save version ' + escapeHtml(state.version + 1) + '</button>' +
      '<button type="button" class="btn-text" data-discard' + (state.dirty ? '' : ' disabled') + '>Discard changes</button>' +
      '<span class="advisor-admin__size' + (size.fits ? '' : ' is-over') + '">' + (size.bytes / 1024).toFixed(1) + ' of 8 KB</span>' +
      '<p class="advisor-admin__status advisor-admin__status--' + escapeHtml(state.statusKind || 'info') + '" role="status" data-status>' + escapeHtml(state.status) + '</p>' +
      '</section>' +
      '<section class="advisor-admin__section"><h2>Preview by state</h2><div data-preview-slot>' + previewHtml(state.draft, state.preview) + '</div></section>' +
      '<section class="advisor-admin__section"><h2>Version history</h2>' + historyHtml(state.history) + '</section>';
    wire();
  }

  function changed(status) {
    state.dirty = true;
    state.status = status || '';
    state.statusKind = 'info';
    render();
  }

  function wire() {
    root.querySelectorAll('[data-remove-rule]').forEach((btn) => {
      btn.addEventListener('click', () => {
        state.draft.rules.splice(Number(btn.getAttribute('data-remove-rule')), 1);
        changed('Rule removed. Save to apply it.');
      });
    });
    const add = root.querySelector('[data-add-rule]');
    add.addEventListener('submit', (e) => {
      e.preventDefault();
      const fd = new FormData(add);
      const values = Object.fromEntries(fd.entries());
      const out = ruleFromForm(values, state.draft.rules);
      if (out.error) { add.querySelector('[data-add-error]').textContent = out.error; return; }
      state.draft.rules.push(out.rule);
      changed('Rule added. Save to apply it.');
    });
    root.querySelectorAll('.advisor-admin__settings input').forEach((input) => {
      input.addEventListener('change', () => {
        const n = Number(input.value);
        if (!Number.isFinite(n) || n < 0 || n > 100) { input.value = input.name === 'fitFloor' ? state.draft.fitFloor : state.draft.weights[input.name.slice(7)]; return; }
        if (input.name === 'fitFloor') state.draft.fitFloor = n;
        else state.draft.weights[input.name.slice(7)] = n;
        changed('Settings changed. Save to apply them.');
      });
    });
    const onPreview = () => {
      const fd = new FormData(root.querySelector('[data-preview]')); // re-rendered on every change
      state.preview = Object.fromEntries(fd.entries());
      const slot = root.querySelector('[data-preview-slot]');
      const focusName = document.activeElement && document.activeElement.name;
      slot.innerHTML = previewHtml(state.draft, state.preview);
      wirePreview();
      const again = focusName && slot.querySelector('[name="' + focusName + '"]');
      if (again) {
        again.focus();
        if (typeof again.setSelectionRange === 'function' && again.type === 'text') again.setSelectionRange(again.value.length, again.value.length);
      }
    };
    function wirePreview() {
      const form = root.querySelector('[data-preview]');
      form.addEventListener('input', onPreviewDebounced);
      form.addEventListener('change', onPreview);
      form.addEventListener('submit', (e) => e.preventDefault());
    }
    let timer = null;
    function onPreviewDebounced() { clearTimeout(timer); timer = setTimeout(onPreview, 250); }
    wirePreview();

    const discard = root.querySelector('[data-discard]');
    discard.addEventListener('click', () => {
      state.draft = { rules: stored.rules.map((r) => ({ ...r })), fitFloor: stored.fitFloor, weights: { ...stored.weights } };
      state.dirty = false;
      state.status = 'Changes discarded.';
      state.statusKind = 'info';
      render();
    });
    const save = root.querySelector('[data-save]');
    save.addEventListener('click', async () => {
      save.disabled = true;
      state.status = 'Saving…';
      root.querySelector('[data-status]').textContent = state.status;
      const res = await putRuleSet(state.version, state.draft, fetchImpl);
      if (res.ok) {
        const saved = readRuleSet(res.ruleSet);
        stored.rules = saved.rules; stored.fitFloor = saved.fitFloor; stored.weights = saved.weights; stored.version = saved.version;
        state.version = saved.version;
        state.draft = { rules: saved.rules.map((r) => ({ ...r })), fitFloor: saved.fitFloor, weights: { ...saved.weights } };
        state.dirty = false;
        state.status = 'Saved version ' + saved.version + '. Agents get it on their next quote.';
        state.statusKind = 'ok';
        const fresh = await fetchAdminData(fetchImpl);
        if (!fresh.status && fresh.history) state.history = fresh.history;
      } else {
        state.statusKind = 'error';
        state.status = res.status === 409
          ? 'Another admin saved a newer version' + (res.currentVersion != null ? ' (' + res.currentVersion + ')' : '') + '. Reload the page to see it, then make your change again.'
          : res.message + (res.errors && res.errors.length ? ': ' + res.errors.map((e) => e.field + ' ' + e.message).join('; ') : '');
      }
      render();
    });
  }

  render();
}

export const AdvisorAdmin = {
  MAX_BODY_BYTES, PRODUCT_TYPE_LABELS, WEIGHT_LABELS, KNOWN_CARRIERS, DEFAULT_FIT_FLOOR,
  describeTarget, describeAction, ruleFromForm, rulesForState, previewPlan, savePayload,
  fetchAdminData, putRuleSet, mountAdmin,
};
